import React from 'react'
import {Box, Stack, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {getVendorGatewayMeta} from '../../constants/vendorGatewayLogos'
import VendorGatewayLogo from './VendorGatewayLogo'
import EnvironmentBadge from './EnvironmentBadge'

export type GatewayMetricRow = {
  factory_name: string
  environment?: string | null
  attempts: number
  succeeded: number
  failed: number
  success_rate: number
  gross_volume: number
  currency: string
  last_success_at?: string | null
}

export type MetricsPayload = {
  period: string
  gateways: GatewayMetricRow[]
  totals: {
    attempts: number
    succeeded: number
    failed: number
    success_rate: number
    gross_volume: number
    currency: string
  }
}

function MetricStat({label, value}: {label: string; value: string}) {
  return (
    <Stack gap='2px' minWidth={72}>
      <Typography fontSize={11} color='var(--bs-gray-600)' textTransform='uppercase'>
        {label}
      </Typography>
      <Typography fontSize={16} fontWeight={700} color='var(--bs-text-dark)'>
        {value}
      </Typography>
    </Stack>
  )
}

export default function GatewayMetricsCards({
  metrics,
  isLoading,
}: {
  metrics: MetricsPayload | null
  isLoading?: boolean
}) {
  const {t} = useTranslation()

  if (isLoading) {
    return (
      <Typography color='var(--bs-gray-600)' fontSize={14}>
        {t('payment.metrics_loading') as string}
      </Typography>
    )
  }

  if (!metrics || metrics.gateways.length === 0) {
    return (
      <Box p='20px' border='1px dashed var(--bs-gray-300)' borderRadius='8px' textAlign='center'>
        <Typography color='var(--bs-gray-600)'>{t('payment.metrics_empty') as string}</Typography>
      </Box>
    )
  }

  return (
    <Stack gap='12px'>
      <Box
        p='16px'
        borderRadius='8px'
        sx={{backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0'}}
      >
        <Stack direction='row' gap={3} flexWrap='wrap'>
          <MetricStat
            label={t('payment.success_rate') as string}
            value={`${metrics.totals.success_rate}%`}
          />
          <MetricStat
            label={t('payment.attempts') as string}
            value={String(metrics.totals.attempts)}
          />
          <MetricStat
            label={t('payment.gross_volume') as string}
            value={`${metrics.totals.gross_volume} ${metrics.totals.currency}`}
          />
        </Stack>
      </Box>

      {metrics.gateways.map((row) => {
        const meta = getVendorGatewayMeta(row.factory_name)
        return (
          <Box
            key={`${row.factory_name}-${row.environment || 'na'}`}
            p='16px'
            border='1px solid var(--bs-gray-300)'
            borderRadius='8px'
          >
            <Stack
              direction={{xs: 'column', sm: 'row'}}
              justifyContent='space-between'
              gap='12px'
              alignItems={{xs: 'flex-start', sm: 'center'}}
            >
              <Stack direction='row' gap='10px' alignItems='center'>
                <VendorGatewayLogo factoryName={row.factory_name} size={40} />
                <Stack gap='4px'>
                  <Typography fontWeight={600} color='var(--bs-text-dark)'>
                    {meta?.name || row.factory_name}
                  </Typography>
                  <EnvironmentBadge
                    environment={(row.environment as 'sandbox' | 'live') || undefined}
                    size='sm'
                  />
                </Stack>
              </Stack>
              <Stack direction='row' gap={3} flexWrap='wrap'>
                <MetricStat
                  label={t('payment.success_rate') as string}
                  value={`${row.success_rate}%`}
                />
                <MetricStat label={t('payment.attempts') as string} value={String(row.attempts)} />
                <MetricStat
                  label={t('payment.gross_volume') as string}
                  value={`${row.gross_volume} ${row.currency}`}
                />
              </Stack>
            </Stack>
          </Box>
        )
      })}
    </Stack>
  )
}
